import React, { useState, useEffect, useRef, useCallback } from 'react';
import { pehchanpeAPI, pehchanpeEkycAPI } from '../utils/apiUtils';
import { SUPPORTED_BANKS } from '../config/apiConfig';
import SearchableDropdown from './SearchableDropdown';
import { CreditBureauResults } from './CreditBureauResults';
import { transformCreditBureauData } from '../utils/creditBureauUtils';
import BankStatementResults from './BankStatementResults';
import { transformBSAData, getAnalysisDateRange } from '../utils/bankStatementUtils';
import ProcessingProgress from './ProcessingProgress';
import CheckStatusProgress from './CheckStatusProgress';
import { 
  DrivingLicenseResult, 
  PANVerificationResult, 
  VoterIDResult, 
  FSSAIResult, 
  DrugLicenseResult 
} from './EkycResults';

interface ServiceFormProps {
  service: {
    id: string;
    name: string;
    description: string;
    category: string;
    endpoints: string[];
  };
  onResult: (result: string | React.ReactNode) => void;
  onLoading: (loading: boolean) => void;
}

export const ServiceForm: React.FC<ServiceFormProps> = ({ service, onResult, onLoading }) => {
  const [formData, setFormData] = useState<any>({});
  const [files, setFiles] = useState<FileList | null>(null);
  
  // States array sorted alphabetically by state name
  const states = [
    { code: 'AN', name: 'Andaman and Nicobar Islands' },
    { code: 'AP', name: 'Andhra Pradesh' },
    { code: 'AR', name: 'Arunachal Pradesh' },
    { code: 'AS', name: 'Assam' },
    { code: 'CH', name: 'Chandigarh' },
    { code: 'CT', name: 'Chhattisgarh' },
    { code: 'DD', name: 'Daman and Diu' },
    { code: 'DL', name: 'Delhi' },
    { code: 'DN', name: 'Dadra and Nagar Haveli' },
    { code: 'GA', name: 'Goa' },
    { code: 'GJ', name: 'Gujarat' },
    { code: 'HP', name: 'Himachal Pradesh' },
    { code: 'HR', name: 'Haryana' },
    { code: 'JH', name: 'Jharkhand' },
    { code: 'JK', name: 'Jammu and Kashmir' },
    { code: 'KA', name: 'Karnataka' },
    { code: 'KL', name: 'Kerala' },
    { code: 'LD', name: 'Lakshadweep' },
    { code: 'MH', name: 'Maharashtra' },
    { code: 'ML', name: 'Meghalaya' },
    { code: 'MN', name: 'Manipur' },
    { code: 'MP', name: 'Madhya Pradesh' },
    { code: 'MZ', name: 'Mizoram' },
    { code: 'NL', name: 'Nagaland' },
    { code: 'OR', name: 'Odisha' },
    { code: 'PB', name: 'Punjab' },
    { code: 'PY', name: 'Puducherry' },
    { code: 'RJ', name: 'Rajasthan' },
    { code: 'SK', name: 'Sikkim' },
    { code: 'TG', name: 'Telangana' },
    { code: 'TN', name: 'Tamil Nadu' },
    { code: 'TR', name: 'Tripura' },
    { code: 'UP', name: 'Uttar Pradesh' },
    { code: 'UT', name: 'Uttarakhand' },
    { code: 'WB', name: 'West Bengal' }
  ];
  
  // Progress tracking state
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState('');
  const [progressAttempt] = useState(0);
  const [progressError, setProgressError] = useState<string | null>(null);
  
  // Check Status Progress States
  const [showCheckStatusProgress, setShowCheckStatusProgress] = useState(false);
  const [checkStatusProgress, setCheckStatusProgress] = useState(0);
  const [checkStatusMessage, setCheckStatusMessage] = useState('');
  const [checkStatusError, setCheckStatusError] = useState<string | null>(null);
  
  // Bank statement specific UI state
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [analysisInProgress, setAnalysisInProgress] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [pendingBadId, setPendingBadId] = useState<string | null>(null);
  const statusCheckedRef = useRef(false); // Use ref to prevent multiple status checks

  // Utility functions for bad_id storage
  const storeBadId = (badId: string) => {
    localStorage.setItem('pehchanpe_pending_bad_id', badId);
    localStorage.setItem('pehchanpe_bad_id_timestamp', Date.now().toString());
  };

  const getPendingBadId = () => {
    const badId = localStorage.getItem('pehchanpe_pending_bad_id');
    const timestamp = localStorage.getItem('pehchanpe_bad_id_timestamp');
    
    if (badId && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      const ageHours = age / (1000 * 60 * 60);
      
      console.log(`Found stored bad_id: ${badId}, age: ${ageHours.toFixed(2)} hours`);
      
      // Only consider bad_id valid for 24 hours
      if (age < 24 * 60 * 60 * 1000) {
        console.log('bad_id is within 24-hour expiry, returning it');
        return badId;
      } else {
        console.log('bad_id expired (>24 hours), cleaning up');
        // Clean up expired bad_id
        clearPendingBadId();
      }
    } else {
      console.log('No stored bad_id found in localStorage');
    }
    return null;
  };

  const clearPendingBadId = () => {
    localStorage.removeItem('pehchanpe_pending_bad_id');
    localStorage.removeItem('pehchanpe_bad_id_timestamp');
  };

  // Check for pending analysis on component mount
  useEffect(() => {
    if (service.id === 'bank-statement-analysis' && !statusCheckedRef.current) {
      const stored = getPendingBadId();
      if (stored) {
        setPendingBadId(stored);
        setAnalysisInProgress(true);
        setUploadCompleted(true);
        statusCheckedRef.current = true; // Mark as checked to prevent multiple calls
        
        // Automatically check status when page loads with stored bad_id
        checkStoredAnalysisStatus(stored);
      } else {
        statusCheckedRef.current = true; // Mark as checked even if no stored bad_id
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.id]);

  // Function to automatically check stored analysis status on page load
  const checkStoredAnalysisStatus = useCallback(async (badId: string) => {
    if (!badId) {
      console.log('No badId provided to checkStoredAnalysisStatus');
      return;
    }
    
    console.log('Checking stored analysis status for badId:', badId);
    try {
      const statusCheck = await pehchanpeAPI.checkAnalysisStatusByBadId(badId);
      
      // Check if the analysis is still being processed (not found in getAllBankAccounts yet)
      if (statusCheck.status === 'processing' && !statusCheck.account) {
        console.log('Analysis still being processed, not found in getAllBankAccounts yet');
        // Keep the reminder card so user can manually check later
        return;
      }
      
      // If analysis is complete, automatically show results
      if (statusCheck.isComplete) {
        console.log('Found completed analysis on page load, fetching results...');
        
        // Get analysis data
        const { startDate, endDate } = getAnalysisDateRange();
        const analysisResult = await pehchanpeAPI.getBSAAnalysis(statusCheck.account.bad_id.toString(), startDate, endDate);
        
        let transactionsResult;
        try {
          transactionsResult = await pehchanpeAPI.getBSATransactions(statusCheck.account.bad_id.toString(), startDate, endDate);
        } catch (error) {
          console.warn('Transactions endpoint not available, using analysis data only:', error);
          transactionsResult = null;
        }
        
        // Transform and display results automatically
        const transformedData = transformBSAData(analysisResult, transactionsResult);
        
        // Clear pending analysis and show results
        clearPendingBadId();
        setAnalysisInProgress(false);
                setShowForm(false);
        
        onResult(
          <div className="w-full">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 mb-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Bank Statement Analysis Results</h2>
              <button
                onClick={() => {
                  setShowForm(true);
                  onResult('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Start New Analysis
              </button>
            </div>
            <BankStatementResults data={transformedData} />
          </div>
        );
      } else {
        // Analysis still in progress - show current progress
        console.log(`Analysis still in progress: ${statusCheck.progress}% complete`);
      }
    } catch (error: any) {
      console.error('Failed to check stored analysis status:', error);
      
      // If the analysis is not found or service is down, show error in the reminder card
      if (error.message?.includes('not found')) {
        console.log('Stored analysis not found, clearing from localStorage');
        clearPendingBadId();
        setPendingBadId(null);
        setAnalysisInProgress(false);
        setUploadCompleted(false);
        setShowForm(true);
        // Don't reset statusChecked here to prevent re-running the check
      } else if (error.message?.includes('Network connection failed')) {
        console.log('Network error when checking analysis status, keeping reminder card for manual retry');
        // Keep the reminder card so user can manually retry
        // Don't clear localStorage or reset states
      }
      // For other errors (like 500), just keep showing the reminder card
      // User can manually click "Check Status" to see the error
    }
  }, [onResult]);

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiles(e.target.files);
  };

  // Function to check analysis status for existing bad_id
  const checkAnalysisStatus = async () => {
    if (!pendingBadId) return;
    
    setShowCheckStatusProgress(true);
    setCheckStatusProgress(25);
    setCheckStatusMessage('Connecting to server...');
    
    try {
      // Single API call to check if analysis is ready
      setCheckStatusProgress(50);
      setCheckStatusMessage('Checking analysis status...');
      const statusCheck = await pehchanpeAPI.checkAnalysisStatusByBadId(pendingBadId);
      
      // Check if the analysis is still being processed (not found in getAllBankAccounts yet)
      if (statusCheck.status === 'processing' && !statusCheck.account) {
        setCheckStatusProgress(75);
        setCheckStatusMessage(statusCheck.message || 'Analysis is still being processed. Please wait a few minutes and try again.');
        
        // Wait a moment to show progress, then hide
        setTimeout(() => {
          setShowCheckStatusProgress(false);
          setCheckStatusError(null);
        }, 3000);
        return;
      }
      
      // Check if processing is complete
      if (!statusCheck.isComplete) {
        setCheckStatusProgress(75);
        setCheckStatusMessage(`Analysis still in progress: ${statusCheck.progress || 0}% complete`);
        
        // Wait a moment to show progress, then hide
        setTimeout(() => {
          setShowCheckStatusProgress(false);
          setCheckStatusError(null);
        }, 2000);
        return;
      }
      
      // Analysis is complete - fetch the results
      setCheckStatusProgress(90);
      setCheckStatusMessage('Analysis complete! Fetching results...');
      
      // Get analysis data using the bad_id as account identifier
      const { startDate, endDate } = getAnalysisDateRange();
      
      let analysisResult;
      try {
        analysisResult = await pehchanpeAPI.getBSAAnalysis(statusCheck.account.bad_id.toString(), startDate, endDate);
      } catch (analysisError: any) {
        if (analysisError.status === 500) {
          setCheckStatusError('Analysis data is temporarily unavailable. The service may be under maintenance. Please try again later.');
          return;
        }
        throw analysisError;
      }
      
      let transactionsResult;
      try {
        transactionsResult = await pehchanpeAPI.getBSATransactions(statusCheck.account.bad_id.toString(), startDate, endDate);
      } catch (error) {
        console.warn('Transactions endpoint not available, using analysis data only:', error);
        transactionsResult = null;
      }
      
      // Transform and display results
      const transformedData = transformBSAData(analysisResult, transactionsResult);
      
      // Clear pending analysis and show results
      clearPendingBadId();
      setAnalysisInProgress(false);
      setShowForm(false);
      setShowCheckStatusProgress(false);
        
        onResult(
          <div className="w-full">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 mb-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Bank Statement Analysis Results</h2>
              <button
                onClick={() => {
                  setShowForm(true);
                  onResult('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Start New Analysis
              </button>
            </div>
            <BankStatementResults data={transformedData} />
          </div>
        );
      
    } catch (error: any) {
      console.error('Failed to check analysis status:', error);
      
      let errorMessage = 'Failed to retrieve analysis';
      
      // Handle specific error types
      if (error.status === 500) {
        errorMessage = 'The analysis service is temporarily unavailable. Please try again in a few minutes.';
      } else if (error.status === 404) {
        errorMessage = 'Analysis not found. It may have expired or been removed.';
      } else if (error.message?.includes('not found')) {
        errorMessage = 'Analysis not found. It may have expired or been removed.';
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setCheckStatusError(errorMessage);
    }
  };

  // Function to start new analysis (cancel current one)
  const startNewAnalysis = () => {
    clearPendingBadId();
    setPendingBadId(null);
    setAnalysisInProgress(false);
    setUploadCompleted(false);
    setShowForm(true);
    statusCheckedRef.current = false; // Allow checking status again for new analysis
  };

  const executeAPI = async () => {
    onLoading(true);
    onResult('');
    
    try {
      let result;
      
      switch (service.id) {
        case 'bank-statement-analysis':
          // Validate required fields
          if (!files || files.length === 0) {
            throw new Error('Please select at least one PDF file to upload');
          }
          if (!formData.bankname) {
            throw new Error('Please select a bank name');
          }
          if (!formData.accountType) {
            throw new Error('Please select an account type');
          }

          // Initialize progress tracking
          setShowProgress(true);
          setProgress(0);
          setProgressStatus('Initializing upload...');
          setProgressError(null);
          
          // Step 1: Parse PDF Files
          setProgress(5);
          setProgressStatus('Uploading and parsing PDF files...');
          
          const parseResult = await pehchanpeAPI.parsePDF({
            files: Array.from(files),
            bankname: formData.bankname,
            accountType: formData.accountType as 'Savings' | 'Current' | 'CC',
            password: formData.password || '',
            names: '--'
          });
          
          console.log('Parse result:', parseResult);
          setProgress(100);
          setProgressStatus('Statement Upload Completed! Analysis in Progress...');
          
          // Store bad_id for future reference
          if (parseResult?.bad_id) {
            storeBadId(parseResult.bad_id);
            setPendingBadId(parseResult.bad_id);
          }
          
          // Wait for user to see upload completion
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Hide progress dialog and show analysis in progress message
          setShowProgress(false);
          setUploadCompleted(true);
          setAnalysisInProgress(true);
          onLoading(false); // Stop loading spinner in parent
          
          // Don't start background polling - let user use "Check Status" button instead
          console.log('Upload completed. User can use "Check Status" button to check when analysis is ready.');
          
          // Early return to avoid the normal result handling
          return;

        case 'credit-bureau':
          // Validate required fields
          if (!formData.name || !formData.pan || !formData.gender || !formData.mobile) {
            throw new Error('Please fill in all required fields: Name, PAN, Gender, and Mobile');
          }
          if (formData.consent !== 'Y') {
            throw new Error('Please provide consent to access credit information');
          }
          
          const creditBureauData = {
            name: formData.name,
            pan: formData.pan,
            gender: formData.gender,
            mobile: formData.mobile,
            consent: formData.consent || 'Y'
          };
          
          console.log('Sending credit bureau data:', creditBureauData);
          result = await pehchanpeAPI.getCreditReport(creditBureauData);
          break;

        case 'driving-license':
          result = await pehchanpeEkycAPI.verifyDrivingLicense({
            id_number: formData.id_number,
            dob: formData.dob
          });
          break;

        case 'pan-verification':
          if (formData.action === 'panDetails') {
            result = await pehchanpeEkycAPI.getPANDetails({
              panNumber: formData.panNumber
            });
          } /* else if (formData.action === 'caVerification') {
            result = await pehchanpeEkycAPI.verifyCA({
              panNumber: formData.panNumber
            });
          } */
          break;

        case 'voter-id':
          result = await pehchanpeEkycAPI.verifyVoterID({
            id_number: formData.id_number,
            state_code: formData.state_code
          });
          break;

        case 'fssai-verification':
          result = await pehchanpeEkycAPI.verifyFSSAI({
            id_number: formData.id_number
          });
          break;

        case 'drug-license':
          result = await pehchanpeEkycAPI.verifyDrugLicense({
            state_code: formData.state_code,
            license_no: formData.license_no
          });
          break;

        default:
          throw new Error('Service not implemented');
      }

      // Handle result display
      if (React.isValidElement(result)) {
        // If result is already a React component, display it directly
        onResult(result);
      } else if (service.id === 'credit-bureau') {
        try {
          const enhancedData = transformCreditBureauData(result);
          onResult(<CreditBureauResults data={enhancedData} />);
        } catch (transformError) {
          // If transformation fails, show enhanced JSON
          onResult(
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Raw API Response:</h4>
              <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          );
        }
      } else if (service.id === 'driving-license') {
        onResult(<DrivingLicenseResult data={result} />);
      } else if (service.id === 'pan-verification') {
        onResult(<PANVerificationResult data={result} />);
      } else if (service.id === 'voter-id') {
        onResult(<VoterIDResult data={result} />);
      } else if (service.id === 'fssai-verification') {
        onResult(<FSSAIResult data={result} />);
      } else if (service.id === 'drug-license') {
        onResult(<DrugLicenseResult data={result} />);
      } else {
        // For other services, show formatted JSON wrapped in proper JSX
        onResult(
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
            <h4 className="text-sm font-medium text-gray-900 mb-2">API Response:</h4>
            <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-x-auto">
              {typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result)}
            </pre>
          </div>
        );
      }
    } catch (error: any) {
      // Enhanced error display for better user experience
      let errorMessage = `❌ ${error.message}`;
      
      // If it's an API error with structured data, also show raw response for debugging
      if (error.name === 'APIError' && error.data) {
        errorMessage += `\n\n📋 Raw API Response (for debugging):`;
        errorMessage += `\n${JSON.stringify(error.data, null, 2)}`;
        
        // For credit bureau errors with partial data, try to show enhanced view
        if (service.id === 'credit-bureau' && error.data) {
          try {
            const enhancedData = transformCreditBureauData(error.data);
            errorMessage = `❌ ${error.message}`;
            onResult(
              <div>
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-semibold text-red-900 mb-2">API Error</h4>
                  <p className="text-red-800">{error.message}</p>
                </div>
                <CreditBureauResults data={enhancedData} />
              </div>
            );
            return;
          } catch {
            // Fall back to text error if enhancement fails
          }
        }

        // Handle bank statement analysis errors with more specific messages
        if (service.id === 'bank-statement-analysis') {
          if (error.message.includes('timeout') || error.message.includes('Processing timeout')) {
            errorMessage = `Processing Timeout\n\nThe bank statement analysis is taking longer than expected. This can happen with:\n• Large PDF files\n• Complex bank statements\n• High server load\n\nPlease try again with smaller files or retry later.`;
          } else if (error.status === 422) {
            errorMessage = `Invalid File Format\n\nPlease ensure:\n• Files are valid PDF bank statements\n• PDF password is correct (if required)\n• Bank name matches the statement issuer\n• Account type is correct\n• Files are not corrupted`;
          } else if (error.status === 400) {
            errorMessage = `Invalid Request\n\nPlease check:\n• All required fields are filled\n• Files are properly selected\n• Bank name and account type are correct`;
          }
          
          // Show error in progress dialog
          setProgressError(errorMessage);
          return; // Don't proceed with normal error handling
        }
      }
      
      onResult(errorMessage);
    } finally {
      onLoading(false);
      // Only hide progress for non-bank statement analysis or if no error
      if (service.id !== 'bank-statement-analysis' || !progressError) {
        setShowProgress(false);
      }
    }
  };

  const renderForm = () => {
    switch (service.id) {
      case 'bank-statement-analysis':
        return (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 Quick Start Guide</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Upload one or multiple PDF bank statements</li>
                <li>• Select the correct bank name and account type</li>
                <li>• Analysis covers the last 2 years of data</li>
                <li>• <span className="font-medium">Upload completes quickly</span> - analysis continues in background (2-5 minutes)</li>
                <li>• You can safely close and return later to check results</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PDF Bank Statement Files <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Select one or more PDF files</p>
            </div>

            <SearchableDropdown
              options={SUPPORTED_BANKS}
              value={formData.bankname || ''}
              onChange={(value) => handleInputChange('bankname', value)}
              label="Bank Name"
              placeholder="Type to search banks (e.g., HDFC, SBI, ICICI)..."
              required={true}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Start typing your bank name to search from {SUPPORTED_BANKS.length} supported banks
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.accountType || ''}
                onChange={(e) => handleInputChange('accountType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select Account Type</option>
                <option value="Savings">Savings Account</option>
                <option value="Current">Current Account</option>
                <option value="CC">Credit Card</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PDF Password (optional)</label>
              <input
                type="password"
                value={formData.password || ''}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Leave empty if no password required"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">Only required if PDF is password protected</p>
            </div>
          </div>
        );

      case 'credit-bureau':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PAN Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.pan || ''}
                onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                placeholder="ABCDE1234F"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.gender || ''}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.mobile || ''}
                onChange={(e) => handleInputChange('mobile', e.target.value)}
                placeholder="10-digit mobile number"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div className="mt-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 pt-0.5">
                    <input
                      id="credit-consent"
                      type="checkbox"
                      checked={formData.consent === 'Y'}
                      onChange={(e) => handleInputChange('consent', e.target.checked ? 'Y' : 'N')}
                      className="h-5 w-5 text-indigo-600 border-2 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 focus:ring-offset-2 transition-colors duration-200"
                      required
                    />
                  </div>
                  <label htmlFor="credit-consent" className="flex-1 cursor-pointer">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <p className="text-sm font-semibold text-gray-900 leading-relaxed">
                          Credit Information Consent <span className="text-red-500 ml-1">*</span>
                        </p>
                      </div>
                      <p className="text-sm text-gray-800 leading-relaxed">
                        I hereby give my consent to access my credit information from credit bureaus for analysis purposes. I understand that this information will be used solely for generating credit insights and recommendations, and will be handled in accordance with applicable data protection regulations.
                      </p>
                    </div>
                  </label>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-100">
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="text-xs text-blue-700 font-medium">
                      Your data is encrypted and secure. This consent is required to proceed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'driving-license':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">License Number</label>
              <input
                type="text"
                value={formData.id_number || ''}
                onChange={(e) => handleInputChange('id_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
              <input
                type="text"
                value={formData.dob || ''}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                placeholder="YYYY-MM-DD"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        );

      case 'pan-verification':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <select
                value={formData.action || ''}
                onChange={(e) => handleInputChange('action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Action</option>
                <option value="panDetails">Get PAN Details</option>
                {/* <option value="caVerification">CA Verification</option> */}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number</label>
              <input
                type="text"
                value={formData.panNumber || ''}
                onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
                placeholder="ABCDE1234F"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        );

      case 'voter-id':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voter ID Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.id_number || ''}
                onChange={(e) => handleInputChange('id_number', e.target.value.toUpperCase())}
                placeholder="ABC1234567"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State Code <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.state_code || ''}
                onChange={(e) => handleInputChange('state_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'fssai-verification':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                FSSAI License Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.id_number || ''}
                onChange={(e) => handleInputChange('id_number', e.target.value)}
                placeholder="12345678901234"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Enter 14-digit FSSAI license number</p>
            </div>
          </div>
        );

      case 'drug-license':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State Code <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.state_code || ''}
                onChange={(e) => handleInputChange('state_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select State</option>
                <option value="MH">Maharashtra</option>
                <option value="DL">Delhi</option>
                <option value="KA">Karnataka</option>
                <option value="TN">Tamil Nadu</option>
                <option value="GJ">Gujarat</option>
                <option value="UP">Uttar Pradesh</option>
                <option value="WB">West Bengal</option>
                <option value="RJ">Rajasthan</option>
                <option value="MP">Madhya Pradesh</option>
                <option value="AP">Andhra Pradesh</option>
                <option value="TG">Telangana</option>
                <option value="OR">Odisha</option>
                <option value="KL">Kerala</option>
                <option value="JH">Jharkhand</option>
                <option value="AS">Assam</option>
                <option value="PB">Punjab</option>
                <option value="CT">Chhattisgarh</option>
                <option value="HR">Haryana</option>
                <option value="JK">Jammu and Kashmir</option>
                <option value="UT">Uttarakhand</option>
                <option value="HP">Himachal Pradesh</option>
                <option value="TR">Tripura</option>
                <option value="ML">Meghalaya</option>
                <option value="MN">Manipur</option>
                <option value="NL">Nagaland</option>
                <option value="GA">Goa</option>
                <option value="AR">Arunachal Pradesh</option>
                <option value="MZ">Mizoram</option>
                <option value="SK">Sikkim</option>
                <option value="PY">Puducherry</option>
                <option value="CH">Chandigarh</option>
                <option value="AN">Andaman and Nicobar Islands</option>
                <option value="DN">Dadra and Nagar Haveli</option>
                <option value="DD">Daman and Diu</option>
                <option value="LD">Lakshadweep</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                License Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.license_no || ''}
                onChange={(e) => handleInputChange('license_no', e.target.value)}
                placeholder="DL123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Enter drug license number for the selected state</p>
            </div>
          </div>
        );

      // Add more service forms here...
      default:
        return (
          <div className="text-gray-500">
            Service form not yet implemented. Please check back later.
          </div>
        );
    }
  };

  // Render pending analysis reminder for bank statement
  const renderPendingAnalysisReminder = () => (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Analysis in Progress
          </h3>
          <p className="text-sm text-gray-700 mb-4">
            Your bank statement analysis is currently being processed. This may take up to 5 minutes. Use the "Check Status" button to see when it's ready.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={checkAnalysisStatus}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Check Status</span>
            </button>
            <button
              onClick={startNewAnalysis}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Start New Analysis
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render analysis in progress message
  const renderAnalysisInProgress = () => (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Statement Upload Completed!
          </h3>
          <p className="text-sm text-gray-700 mb-3">
            Analysis in Progress... it may take up to 5 minutes. Use the "Check Status" button to see when it's ready.
          </p>
          <p className="text-xs text-gray-500 mb-4">
            You can safely close this window. Your analysis will be available when you return.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={checkAnalysisStatus}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Check Status</span>
            </button>
            <button
              onClick={startNewAnalysis}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Start New Analysis
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Show pending analysis reminder if there's a stored bad_id */}
      {service.id === 'bank-statement-analysis' && pendingBadId && !uploadCompleted && (
        renderPendingAnalysisReminder()
      )}
      
      {/* Show analysis in progress if just completed upload */}
      {service.id === 'bank-statement-analysis' && uploadCompleted && analysisInProgress && (
        renderAnalysisInProgress()
      )}
      
      {/* Show form only if not in analysis state or not bank statement service */}
      {(service.id !== 'bank-statement-analysis' || (showForm && !analysisInProgress)) && (
        <>
          {renderForm()}
          
          <button
            onClick={executeAPI}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 font-medium transition-colors"
          >
            {service.id === 'bank-statement-analysis' 
              ? 'Upload & Start Analysis' 
              : `Test ${service.name}`}
          </button>
        </>
      )}
      
      {/* Progress Dialog */}
      {showProgress && (
        <ProcessingProgress
          progress={progress}
          status={progressStatus}
          attempt={progressAttempt}
          isComplete={progress >= 100 && !progressError}
          hasError={!!progressError}
          errorMessage={progressError || undefined}
        />
      )}
      
      {/* Check Status Progress Dialog */}
      {showCheckStatusProgress && (
        <CheckStatusProgress
          progress={checkStatusProgress}
          status={checkStatusMessage}
          attempt={progressAttempt}
          isComplete={checkStatusProgress >= 100 && !checkStatusError}
          hasError={!!checkStatusError}
          errorMessage={checkStatusError || undefined}
          badId={pendingBadId || undefined}
        />
      )}
    </div>
  );
};
